import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { api, errMsg, money, specValue, uploadFile } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { Amount } from "@/components/Currency";
import { StockBadge } from "@/components/StatusChip";
import { CountrySelect } from "@/components/CountrySelect";
import { Btn, Card, Empty, Field, Input, Loading, Modal, Pill, Select, StatCard, Table, Textarea, STATUS_TONE } from "@/components/Ui";

const SUB_TONE = { pending: "warn", approved: "ok", rejected: "bad", changes_requested: "warn" };
const EDITABLE = ["pending", "changes_requested"];

export default function SupplierProducts() {
  const schema = useSpecSchema();
  const [subs, setSubs] = useState(null);
  const [live, setLive] = useState([]);
  const [modal, setModal] = useState(null);
  const [draft, setDraft] = useState({});
  const specInputs = schema.inputsFor(draft.category);

  const [busy, setBusy] = useState(false);
  const [stockDraft, setStockDraft] = useState({});
  const [savingStock, setSavingStock] = useState("");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [batchNotes, setBatchNotes] = useState("");

  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get("/supplier/submissions"),
        api.get("/supplier/products"),
      ]);
      setSubs(s.data);
      setLive(p.data);
      setStockDraft(p.data.reduce((a, x) => ({
        ...a,
        [x.id]: { stock_quantity: x.stock_quantity ?? "", stock_location: x.stock_location ?? "" },
      }), {}));
    } catch (e) {
      toast.error(errMsg(e));
      setSubs([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStock = (id, patch) =>
    setStockDraft((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const saveStock = async (id) => {
    setSavingStock(id);
    try {
      const draft = stockDraft[id] || {};
      const { data } = await api.put(`/supplier/products/${id}/stock`, {
        stock_quantity: draft.stock_quantity === "" ? null : Number(draft.stock_quantity),
        stock_location: draft.stock_location || null,
      });
      setLive((rows) => rows.map((r) => (r.id === id ? data : r)));
      toast.success("Stock updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingStock(""); }
  };

  const blank = { part_number: "", name: "", manufacturer_name: "", brand: "", product_family: "",
    category: "", subcategory: "", country_of_manufacture: "", technical_notes: "", list_price: "",
    currency: "AED", spec: {}, documents: [] };

  const save = async () => {
    setBusy(true);
    try {
      const spec = {};
      specInputs.forEach(([k, , type]) => {
        const v = draft.spec?.[k];
        if (v !== "" && v != null) spec[k] = type === "number" ? Number(v) : v;
      });
      const body = {
        part_number: draft.part_number, name: draft.name,
        manufacturer_name: draft.manufacturer_name, brand: draft.brand || null,
        product_family: draft.product_family || null, category: draft.category || null,
        subcategory: draft.subcategory || null, description: draft.description || null,
        country_of_manufacture: draft.country_of_manufacture || null,
        technical_notes: draft.technical_notes || null,
        list_price: draft.list_price === "" || draft.list_price == null ? null : Number(draft.list_price),
        currency: draft.currency || "AED",
        datasheet_file_id: draft.datasheet_file_id || null,
        documents: draft.documents || [], spec,
      };
      if (draft.id) await api.put(`/supplier/submissions/${draft.id}`, body);
      else await api.post("/supplier/submissions", body);
      await load();
      setModal(null);
      toast.success("Submitted for ADIT approval");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id) => {
    try {
      await api.delete(`/supplier/submissions/${id}`);
      await load();
      toast.success("Submission withdrawn");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const attachDatasheet = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const up = await uploadFile(f, "datasheet");
      setDraft((d) => ({ ...d, datasheet_file_id: up.file_id,
        documents: [...(d.documents || []), up], datasheet_name: up.filename }));
      toast.success("Datasheet attached");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const downloadTemplate = async (fmt) => {
    try {
      const { data } = await api.get(`/supplier/submissions/template?fmt=${fmt}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `adit-connect-product-template.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const bulkSubmit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (batchNotes.trim()) fd.append("batch_notes", batchNotes.trim());
      const { data } = await api.post("/supplier/submissions/import-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      await load();
      toast.success(`${data.submitted} part number(s) submitted for approval`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!subs) return <Loading />;

  const counts = subs.reduce((a, s) => ({ ...a, [s.status]: (a[s.status] || 0) + 1 }), {});

  return (
    <div data-testid="supplier-products">
      <div className="grid gap-3 sm:grid-cols-4">
        <StatCard testId="stat-live-products" label="Live in catalogue" value={live.length} />
        <StatCard testId="stat-pending-subs" label="Awaiting approval" value={counts.pending || 0} />
        <StatCard testId="stat-changes-subs" label="Changes requested" value={counts.changes_requested || 0} />
        <StatCard testId="stat-rejected-subs" label="Not accepted" value={counts.rejected || 0} />
      </div>

      <Card className="mt-5 bg-[#F5F7FA]">
        <p className="text-sm text-[#334e68]">
          Submit your part numbers and technical data here. Nothing appears in the public
          product catalogue until an ADIT administrator approves it.
        </p>
      </Card>

      <div className="mt-5 flex flex-wrap gap-2">
        <Btn data-testid="add-submission-btn" onClick={() => { setDraft({ ...blank }); setModal("form"); }}>
          <Plus className="h-4 w-4" /> Submit a part number
        </Btn>
        <Btn variant="ghost" data-testid="bulk-submit-btn" onClick={() => { setFile(null); setResult(null); setModal("bulk"); }}>
          <Upload className="h-4 w-4" /> Upload a spreadsheet
        </Btn>
      </div>

      <h2 className="mt-8 font-display text-lg font-bold tracking-tight text-[#102A43]">
        My submissions
      </h2>
      {subs.length === 0 ? (
        <Empty testId="submissions-empty" title="No submissions yet"
          sub="Submit a single part number, or upload a spreadsheet of your range." />
      ) : (
        <Table head={["Part number", "Description", "Manufacturer", "Thread / Gas", "Price", "Status", "Reviewer notes", "Actions"]}
          testId="submissions-table">
          {subs.map((s) => (
            <tr key={s.id} data-testid={`submission-row-${s.id}`}>
              <td className="font-semibold text-[#102A43]">{s.part_number}</td>
              <td>{s.name}</td>
              <td>{s.manufacturer_name}</td>
              <td className="text-xs">{specValue(s.spec, "thread_size")} / {specValue(s.spec, "gas_group")}</td>
              <td>{s.list_price != null ? <Amount value={s.list_price} currency={s.currency} block /> : "—"}</td>
              <td>
                <Pill tone={SUB_TONE[s.status] || "neutral"} testId={`submission-status-${s.id}`}>
                  {s.status.replace(/_/g, " ")}
                </Pill>
              </td>
              <td className="max-w-[220px] text-xs">{s.admin_notes || "—"}</td>
              <td>
                <div className="flex gap-3">
                  {EDITABLE.includes(s.status) && (
                    <>
                      <button data-testid={`edit-submission-${s.id}`} className="text-[#102A43]"
                        onClick={() => { setDraft({ ...s, spec: { ...(s.spec || {}) } }); setModal("form"); }}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button data-testid={`withdraw-submission-${s.id}`} className="text-[#B42318]"
                        onClick={() => withdraw(s.id)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      <h2 className="mt-8 font-display text-lg font-bold tracking-tight text-[#102A43]">
        Live in the ADIT catalogue
      </h2>
      <p className="mt-1 text-sm text-[#667788]">
        Keep stock up to date — buyers see only the status badge and location, never your exact
        quantity. 10 pieces or fewer shows as low stock.
      </p>
      {live.length === 0 ? (
        <Empty testId="live-products-empty" title="Nothing published yet"
          sub="Approved submissions appear here and become searchable by buyers." />
      ) : (
        <Table head={["Part number", "Manufacturer", "Category", "Qty on hand", "Stock location", "Buyer sees", ""]} testId="live-products-table">
          {live.map((p) => (
            <tr key={p.id} data-testid={`live-product-row-${p.id}`}>
              <td className="font-semibold text-[#102A43]">{p.part_number}</td>
              <td>{p.manufacturer_name}</td>
              <td className="text-xs">{p.subcategory || p.category || "—"}</td>
              <td>
                <Input data-testid={`stock-qty-${p.id}`} type="number" min="0"
                  className="mt-0 max-w-[110px]" value={stockDraft[p.id]?.stock_quantity ?? ""}
                  onChange={(e) => setStock(p.id, { stock_quantity: e.target.value })} />
              </td>
              <td>
                <Input data-testid={`stock-location-${p.id}`} className="mt-0 max-w-[200px]"
                  placeholder="e.g. Jebel Ali, Dubai"
                  value={stockDraft[p.id]?.stock_location ?? ""}
                  onChange={(e) => setStock(p.id, { stock_location: e.target.value })} />
              </td>
              <td><StockBadge status={p.stock_status} testId={`stock-badge-${p.id}`} /></td>
              <td>
                <Btn variant="ghost" className="px-3 py-1.5 text-xs" loading={savingStock === p.id}
                  data-testid={`save-stock-${p.id}`} onClick={() => saveStock(p.id)}>
                  Save
                </Btn>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {modal === "form" && (
        <Modal wide title={draft.id ? "Update submission" : "Submit a part number"} onClose={() => setModal(null)}>
          {draft.admin_notes && (
            <p className="mb-4 rounded-[9px] bg-[#fff4df] p-3 text-sm text-[#8a6412]">
              Reviewer notes: {draft.admin_notes}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Part number *"><Input data-testid="sub-part_number" value={draft.part_number || ""} onChange={(e) => setDraft({ ...draft, part_number: e.target.value })} /></Field>
            <Field label="Description / product name *"><Input data-testid="sub-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
            <Field label="Manufacturer *" hint="The manufacturer of the part, not your trading name">
              <Input data-testid="sub-manufacturer" value={draft.manufacturer_name || ""} onChange={(e) => setDraft({ ...draft, manufacturer_name: e.target.value })} />
            </Field>
            <Field label="Brand"><Input data-testid="sub-brand" value={draft.brand || ""} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} /></Field>
            <Field label="Product family"><Input data-testid="sub-family" value={draft.product_family || ""} onChange={(e) => setDraft({ ...draft, product_family: e.target.value })} /></Field>
            <Field label="Category" hint="Determines which technical fields apply below">
              <Select data-testid="sub-category" value={draft.category || ""}
                options={[["", "Select a category"], ...schema.names.map((c) => [c, c])]}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
            </Field>
            <Field label="Subcategory"><Input data-testid="sub-subcategory" value={draft.subcategory || ""} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} /></Field>
            <Field label="Country of manufacture"><CountrySelect testId="sub-country" value={draft.country_of_manufacture || ""} onChange={(v) => setDraft({ ...draft, country_of_manufacture: v })} /></Field>
            <Field label="List price"><Input data-testid="sub-price" type="number" step="0.01" value={draft.list_price ?? ""} onChange={(e) => setDraft({ ...draft, list_price: e.target.value })} /></Field>
            <Field label="Currency"><Select data-testid="sub-currency" value={draft.currency || "AED"} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} options={["AED", "USD", "EUR", "GBP"]} /></Field>
            <Field label="Additional information for buyers" className="sm:col-span-2"
              hint="Anything the columns above miss — what's included, fitting or accessory notes, approvals, packing, minimum order, stock position, or how this differs from a similar part. Buyers see this on the product page, in search results and when they request a quote.">
              <Textarea rows={4} data-testid="sub-notes" maxLength={2000}
                placeholder="e.g. Supplied complete with locknut, earth tag and shroud. IECEx and ATEX certified. Sold in boxes of 10. Ex-stock Dubai."
                value={draft.technical_notes || ""}
                onChange={(e) => setDraft({ ...draft, technical_notes: e.target.value })} />
              <p className="mt-1 text-right text-[10px] text-[#8598ac]" data-testid="sub-notes-count">
                {(draft.technical_notes || "").length}/2000
              </p>
            </Field>
          </div>

          <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
            Technical specification
            {draft.category ? ` · ${draft.category}` : " · set a category above for its own fields"}
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            {specInputs.map(([k, l, type, options]) => (
              <Field key={k} label={l}>
                {options && options.length ? (
                  <Select data-testid={`sub-spec-${k}`} value={draft.spec?.[k] ?? ""}
                    options={[["", "Not stated"], ...options.map((o) => [o, o])]}
                    onChange={(e) => setDraft({ ...draft, spec: { ...draft.spec, [k]: e.target.value } })} />
                ) : (
                  <Input data-testid={`sub-spec-${k}`} type={type} step="0.1" value={draft.spec?.[k] ?? ""}
                    onChange={(e) => setDraft({ ...draft, spec: { ...draft.spec, [k]: e.target.value } })} />
                )}
              </Field>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <input ref={fileRef} data-testid="sub-datasheet-input" type="file" className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={attachDatasheet} />
            <Btn variant="ghost" data-testid="sub-attach-datasheet-btn" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Attach datasheet / certificate
            </Btn>
            <span className="text-xs text-[#667788]">
              {draft.datasheet_name || (draft.datasheet_file_id ? "Datasheet attached" : "No file attached")}
            </span>
          </div>

          <Btn data-testid="save-submission-btn" loading={busy} onClick={save} className="mt-6 w-full">
            {draft.id ? "Resubmit for approval" : "Submit for approval"}
          </Btn>
        </Modal>
      )}

      {modal === "bulk" && (
        <Modal title="Upload a spreadsheet of part numbers" onClose={() => setModal(null)}>
          <div className="rounded-[9px] border border-[#D9A441] bg-[#FDF6E7] p-4">
            <p className="font-display text-sm font-bold text-[#7A5A12]">
              Start with our template
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[#7A5A12]">
              It has every column named correctly, notes on what each one means, and two filled-in
              example rows. Delete the examples, add your products and upload it back.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Btn variant="ghost" data-testid="download-template-xlsx-btn"
                onClick={() => downloadTemplate("xlsx")} className="px-3.5 py-2 text-xs">
                <Download className="h-3.5 w-3.5" /> Excel template (.xlsx)
              </Btn>
              <Btn variant="ghost" data-testid="download-template-csv-btn"
                onClick={() => downloadTemplate("csv")} className="px-3.5 py-2 text-xs">
                <Download className="h-3.5 w-3.5" /> CSV template
              </Btn>
            </div>
          </div>
          <p className="mt-4 text-sm text-[#667788]">
            One row per part number. Required: <strong>part_number</strong> and{" "}
            <strong>manufacturer</strong> — everything else is optional, but the more technical
            columns you fill in, the more buyer searches and equivalence comparisons your products
            appear in. Leave unknown fields blank rather than writing &ldquo;N/A&rdquo;. Every row is
            queued for ADIT approval, and you are emailed once it is reviewed.
          </p>
          <div className="mt-4">
            <Field label="Additional information for buyers (optional)"
              hint="Applied to every row that has no technical_notes of its own — use it for anything the columns cannot express, such as what is included, certification, packing, minimum order or stock position.">
              <Textarea rows={3} data-testid="bulk-notes-input" maxLength={2000}
                placeholder="e.g. All items IECEx and ATEX certified, supplied with locknut and shroud, ex-stock Jebel Ali."
                value={batchNotes} onChange={(e) => setBatchNotes(e.target.value)} />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input id="bulk-file" data-testid="bulk-file-input" type="file" accept=".csv,.xlsx,.xlsm,.txt"
              className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
            <Btn variant="ghost" data-testid="choose-bulk-file-btn"
              onClick={() => document.getElementById("bulk-file").click()}>
              Choose file
            </Btn>
            <span className="text-sm text-[#334e68]" data-testid="bulk-file-name">
              {file ? file.name : "No file selected"}
            </span>
          </div>
          <Btn data-testid="run-bulk-submit-btn" loading={busy} disabled={!file} onClick={bulkSubmit} className="mt-4 w-full">
            Submit for approval
          </Btn>
          {result && (
            <div className="mt-4 rounded-[9px] border border-[#DFE6ED] bg-[#F5F7FA] p-4 text-sm" data-testid="bulk-result">
              <p className="font-semibold text-[#102A43]">
                {result.submitted} submitted · {result.errors?.length || 0} failed
              </p>
              {result.errors?.slice(0, 6).map((e) => (
                <p key={e.row} className="mt-1 text-xs text-[#B42318]">Row {e.row}: {e.error}</p>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
